// calendar.ts - §13 Calendar 의 순수 로직. 그리드 생성 / 이벤트 버킷팅 /
// 하루 과다 이벤트 접기. React 는 여기서 나온 배열을 그리기만 한다.
//
// 주 시작은 **월요일**이다. §03 §6 `week_range()` 가 월요일 기준이므로,
// §4-1 `금주 생산` 카드에서 주간 뷰로 넘어왔을 때 두 화면이 같은 7일을
// 가리켜야 한다. (디자인 가이드의 ASCII 는 일요일 시작으로 그려져 있으나
// 그러면 카드의 건수와 주간 뷰의 건수가 어긋난다.)
import { addDays, isoDate, parseIsoDate } from "@/lib/dates";
import { SEVERITY, type StatusCode, weekRange } from "@/lib/status";

export const WEEKDAY_LABELS: readonly string[] = ["월", "화", "수", "목", "금", "토", "일"];

export interface DayCell {
  /** YYYY-MM-DD */
  date: string;
  /** 1..31 */
  day: number;
  /** 표시 중인 달에 속하는가 (앞뒤 달의 흘러넘친 칸은 false) */
  inMonth: boolean;
  isToday: boolean;
  /** 토(5) · 일(6) */
  isWeekend: boolean;
}

/** "YYYY-MM-DD" -> "YYYY-MM" */
export const monthOf = (iso: string): string => iso.slice(0, 7);

/** "YYYY-MM" 을 delta 개월 이동. 연도 경계를 넘어간다. */
export function shiftMonth(month: string, delta: number): string {
  const y = Number.parseInt(month.slice(0, 4), 10);
  const m = Number.parseInt(month.slice(5, 7), 10);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const cell = (d: Date, month: string, today: string): DayCell => {
  const iso = isoDate(d);
  const dow = (d.getDay() + 6) % 7; // 월=0 … 일=6
  return {
    date: iso,
    day: d.getDate(),
    inMonth: monthOf(iso) === month,
    isToday: iso === today,
    isWeekend: dow >= 5,
  };
};

/**
 * 월간 그리드. 그 달의 1일이 속한 주의 월요일부터 시작해서 필요한 주 수만큼.
 * 앞뒤 달의 칸을 비우지 않고 채우는 이유: 빈 칸이 있으면 달력이 표처럼
 * 읽히지 않는다.
 */
export function monthGrid(month: string, today: string): DayCell[][] {
  const first = parseIsoDate(`${month}-01`);
  const offset = (first.getDay() + 6) % 7;
  const start = addDays(first, -offset);
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const weeks = Math.ceil((offset + daysInMonth) / 7);
  return Array.from({ length: weeks }, (_, w) =>
    Array.from({ length: 7 }, (_, d) => cell(addDays(start, w * 7 + d), month, today))
  );
}

/** 주간 뷰 7일. `anchor` 가 속한 주(월~일). */
export function weekDays(anchor: string, today: string): DayCell[] {
  const [start] = weekRange(anchor);
  const s = parseIsoDate(start);
  const month = monthOf(anchor);
  return Array.from({ length: 7 }, (_, i) => cell(addDays(s, i), month, today));
}

/** 그리드가 실제로 덮는 날짜 범위. `GET /api/calendar?from&to` 에 그대로 넣는다. */
export function gridRange(view: "month" | "week", anchor: string): [string, string] {
  if (view === "week") return weekRange(anchor);
  const grid = monthGrid(monthOf(anchor), anchor);
  return [grid[0][0].date, grid[grid.length - 1][6].date];
}

/* ── 이벤트 배치 ─────────────────────────────────────────────────── */

export interface DatedEvent {
  date: string;
  status: StatusCode;
}

/**
 * 지연 -> 임박 -> 정상 -> 미착수 -> 완료. 관리자가 봐야 하는 것이 항상
 * 먼저 보인다 (REQ-CAL-008). 같은 상태끼리는 입력 순서를 유지한다.
 */
export function sortByUrgency<T extends DatedEvent>(events: readonly T[]): T[] {
  return events
    .map((e, i) => ({ e, i }))
    .sort((a, b) => SEVERITY[b.e.status] - SEVERITY[a.e.status] || a.i - b.i)
    .map((x) => x.e);
}

/** 날짜별 버킷. 각 버킷은 긴급도 순으로 정렬된다. */
export function groupByDate<T extends DatedEvent>(events: readonly T[]): Record<string, T[]> {
  const out: Record<string, T[]> = {};
  for (const e of events) {
    const bucket = out[e.date];
    if (bucket) bucket.push(e);
    else out[e.date] = [e];
  }
  for (const k of Object.keys(out)) out[k] = sortByUrgency(out[k]);
  return out;
}

/**
 * 셀에 최대 `max` 건만 두고 나머지는 `+N건` 으로 접는다.
 * 셀 높이가 늘어나면 월 전체 조망이 깨진다 (결정 S-8).
 */
export function foldEvents<T>(events: readonly T[], max = 3): { shown: T[]; hidden: number } {
  if (events.length <= max) return { shown: [...events], hidden: 0 };
  return { shown: events.slice(0, max), hidden: events.length - max };
}

/** 그리드 제목. "2026년 9월" / 주간은 "2026-09-07 ~ 2026-09-13". */
export function viewTitle(view: "month" | "week", anchor: string): string {
  if (view === "month") {
    const y = anchor.slice(0, 4);
    const m = Number.parseInt(anchor.slice(5, 7), 10);
    return `${y}년 ${m}월`;
  }
  const [s, e] = weekRange(anchor);
  return `${s} ~ ${e}`;
}
